import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw, Terminal, UploadCloud, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

interface BrokerStatus {
  version: string;
  uptimeSeconds: number;
  nodeVersion: string;
  selfUpdateEnabled: boolean;
}

function formatUptime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// Console del broker: nessuna console web ospitata dal broker stesso (è solo-outbound, nessuna
// porta esposta sulla rete del cliente) — questa pagina IS la console, autenticata con la stessa
// sessione del portale (nessun redirect/credenziali separate). Backend e broker comunicano
// passando da Postgres come relay (vedi ingestion/brokerConsole.ts), dato che la connessione mTLS
// live vive nel processo `ingestion`, non in quello che serve queste API REST.
export default function BrokerConsolePage() {
  const { brokerId } = useParams();
  const [status, setStatus] = useState<BrokerStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [logLines, setLogLines] = useState<string[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [targetVersion, setTargetVersion] = useState('');
  const [configPatch, setConfigPatch] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState<{ ok: boolean; message: string } | null>(null);
  const logBoxRef = useRef<HTMLDivElement>(null);

  async function loadStatus() {
    setLoadingStatus(true);
    setStatusError(null);
    try {
      const res = await axios.get(`/api/brokers/${brokerId}/status`);
      setStatus(res.data.data);
    } catch (err: any) {
      setStatusError(err.response?.data?.error || 'Could not reach the broker');
    } finally {
      setLoadingStatus(false);
    }
  }

  useEffect(() => { loadStatus(); }, [brokerId]);

  useEffect(() => {
    const token = localStorage.getItem('soc_token');
    // EventSource non supporta header custom: il token passa in query string, accettato solo
    // come fallback dal middleware di autenticazione (vedi middleware/auth.ts).
    const source = new EventSource(`/api/brokers/${brokerId}/logs?token=${encodeURIComponent(token || '')}`);
    setStreaming(true);
    source.onmessage = e => {
      try {
        const { line } = JSON.parse(e.data);
        setLogLines(prev => [...prev.slice(-499), line]);
      } catch { /* riga malformata, ignorata */ }
    };
    source.onerror = () => { setStreaming(false); };
    return () => { source.close(); setStreaming(false); };
  }, [brokerId]);

  useEffect(() => {
    logBoxRef.current?.scrollTo({ top: logBoxRef.current.scrollHeight });
  }, [logLines]);

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    setUpdating(true);
    setUpdateResult(null);
    let parsedConfig: unknown;
    if (configPatch.trim()) {
      try {
        parsedConfig = JSON.parse(configPatch);
      } catch {
        setUpdateResult({ ok: false, message: 'Config patch must be valid JSON' });
        setUpdating(false);
        return;
      }
    }
    try {
      const res = await axios.post(`/api/brokers/${brokerId}/update`, {
        targetVersion: targetVersion.trim() || undefined,
        configPatch: parsedConfig,
      });
      const { status: resultStatus, message } = res.data.data;
      setUpdateResult({
        ok: resultStatus === 'started',
        message: message || (resultStatus === 'started' ? 'Update started on the broker.' : 'Update acknowledged.'),
      });
    } catch (err: any) {
      setUpdateResult({ ok: false, message: err.response?.data?.error || 'Update request failed' });
    } finally {
      setUpdating(false);
    }
  }

  return (
    <div className="space-y-4">
      <Link to="/sites-brokers" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft size={15} /> Sites & Brokers
      </Link>

      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-semibold text-gray-900">Broker console — #{brokerId}</h1>
          <button onClick={loadStatus} disabled={loadingStatus} className="btn-secondary flex items-center gap-1 disabled:opacity-50">
            <RefreshCw size={13} className={loadingStatus ? 'animate-spin' : ''} /> Refresh status
          </button>
        </div>
        {statusError && (
          <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 inline-flex items-center gap-1.5">
            <AlertTriangle size={14} /> {statusError}
          </div>
        )}
        {status && (
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><dt className="text-xs text-gray-400">Version</dt><dd className="font-medium text-gray-900">{status.version}</dd></div>
            <div><dt className="text-xs text-gray-400">Uptime</dt><dd className="font-medium text-gray-900">{formatUptime(status.uptimeSeconds)}</dd></div>
            <div><dt className="text-xs text-gray-400">Node.js</dt><dd className="font-medium text-gray-900">{status.nodeVersion}</dd></div>
            <div>
              <dt className="text-xs text-gray-400">Self-update</dt>
              <dd className={`badge ${status.selfUpdateEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {status.selfUpdateEnabled ? 'Enabled' : 'Manual only'}
              </dd>
            </div>
          </dl>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2">
            <Terminal size={15} /> Live logs
          </h2>
          <span className={`badge ${streaming ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
            {streaming ? 'Streaming' : 'Disconnected'}
          </span>
        </div>
        <div ref={logBoxRef} className="bg-gray-900 text-gray-100 font-mono text-xs p-3 h-80 overflow-y-auto">
          {logLines.length === 0 && <div className="text-gray-500">Waiting for log lines…</div>}
          {logLines.map((line, i) => <div key={i} className="whitespace-pre-wrap break-all">{line}</div>)}
        </div>
      </div>

      <form onSubmit={handleUpdate} className="card p-4">
        <h2 className="font-medium text-sm text-gray-900 mb-1 inline-flex items-center gap-2">
          <UploadCloud size={15} /> Update
        </h2>
        <p className="text-xs text-gray-400 mb-3">
          Software update requires self-update to be enabled on this broker (an explicit opt-in at
          install time, since it lets the broker replace its own container). Otherwise the request is
          recorded and the broker reports that a manual reinstall is needed. A config patch alone is
          written immediately but only takes effect after the broker is restarted.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Target version (image tag)</label>
            <input value={targetVersion} onChange={e => setTargetVersion(e.target.value)}
              placeholder="e.g. 1.4.0" className="input-field w-full font-mono text-xs" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Config patch (JSON, optional)</label>
            <input value={configPatch} onChange={e => setConfigPatch(e.target.value)}
              placeholder='{"heartbeatSeconds": 30}' className="input-field w-full font-mono text-xs" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          {updateResult && (
            <div className={`text-sm inline-flex items-center gap-1.5 ${updateResult.ok ? 'text-emerald-700' : 'text-amber-700'}`}>
              {updateResult.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {updateResult.message}
            </div>
          )}
          <button type="submit" disabled={updating || (!targetVersion.trim() && !configPatch.trim())}
            className="btn-primary ml-auto disabled:opacity-50">
            {updating ? 'Sending…' : 'Send update'}
          </button>
        </div>
      </form>
    </div>
  );
}
